/**
 * Native-feeling 3-up photo carousel.
 *
 * Renders a strip of `prev | current | next` images that translates as
 * the user drags. On release past the snap threshold, the strip
 * animates to the destination slot; in `onTransitionEnd` the parent's
 * `centerId` is updated and gesture state reset to `dx=0, phase=idle`
 * within the same render — the strip "rebases" at `translateX = -1*W`
 * with the new center, which is the SAME image the animation just
 * landed on, so the swap is visually imperceptible.
 *
 * No React Router navigation per swipe. The parent calls
 * `history.replaceState` after each `onChange`, so the URL stays in
 * sync for deep-linking / share-URL / refresh without remounting the
 * page. The browser back button still exits to /memories normally
 * because we use replace (not push) — there is exactly one history
 * entry for the entire carousel session.
 *
 * Image bytes come from `/api/photos/:id/(thumb|large)` — stable paths
 * with `Cache-Control: immutable`, cached by both the browser and the
 * Workbox service worker. No signed-URL churn, no per-render cache
 * invalidation.
 *
 * Frame fit: the carousel container's aspect-ratio is derived from
 * the center photo's natural dimensions (read off the thumb's
 * naturalWidth/Height once it loads). Portraits and landscapes both
 * fill the frame edge-to-edge — no cream letterbox bands.
 *
 * Thumb→large transition: the slot stacks a blurred thumb underlay
 * with the sharp large variant on top. The large fades in over 200ms
 * via opacity transition, so the quality lift looks like a focus pull
 * rather than a swap. Slots are keyed by photo id so the strip swap
 * never reuses an `<img>` element across photos — every slot mount
 * gets a fresh `<img>` and the onLoad handler is always live.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

interface Siblings {
  prev: { id: string; url: string } | null;
  next: { id: string; url: string } | null;
}

type Phase = 'idle' | 'dragging' | 'snapping';

interface Props {
  centerId: string;
  filterYear: string | null;
  /** When set, swipe stays strictly within this circle's photos. Threads
   *  through to the siblings query key, URL, and the ±2 prefetch chain
   *  so a circle photo never resolves to a family neighbour by accident
   *  (cache poisoning would silently leak family-only photos otherwise). */
  circleId?: string | null;
  /** When set, swipe stays strictly within this memory's ordered photos
   *  (the order on the memory detail page) instead of the family
   *  timeline. Threads through the siblings query key, URL, and prefetch
   *  chain exactly like `circleId`. */
  memoryId?: string | null;
  alt: string;
  onChange: (newId: string) => void;
}

export function PhotoCarousel({
  centerId,
  filterYear,
  circleId,
  memoryId,
  alt,
  onChange,
}: Props) {
  const qc = useQueryClient();
  const siblingsQuery = (() => {
    const qs = new URLSearchParams();
    if (filterYear) qs.set('year', filterYear);
    if (circleId) qs.set('circleId', circleId);
    if (memoryId) qs.set('memoryId', memoryId);
    return qs.toString() ? `?${qs.toString()}` : '';
  })();

  const siblingsQ = useQuery({
    queryKey: [
      'photo-siblings',
      centerId,
      filterYear,
      circleId ?? null,
      memoryId ?? null,
    ],
    queryFn: () => api.get<Siblings>(`/photos/${centerId}/siblings${siblingsQuery}`),
    enabled: !!centerId,
    staleTime: 5 * 60_000,
    // Show the previously-known neighbours while the new ones fetch,
    // so a fast second swipe isn't blocked by a loading state.
    placeholderData: (prev) => prev,
  });

  // Stale placeholder siblings (kept by `placeholderData` while the new
  // center's siblings fetch) describe the PREVIOUS center — and that
  // center's `next` is the very photo we just landed on. Drop any
  // neighbour that equals the current center so we never (a) render two
  // strip slots with the same React key, which breaks the blur→sharp
  // reconciliation and strands the slot on its blurred thumb, nor (b)
  // snap onto the photo already in view. Self-corrects once the real
  // siblings arrive (usually already prefetched, so no visible delay).
  const rawPrevId = siblingsQ.data?.prev?.id ?? null;
  const rawNextId = siblingsQ.data?.next?.id ?? null;
  const prevId = rawPrevId !== centerId ? rawPrevId : null;
  const nextId = rawNextId !== centerId ? rawNextId : null;

  // Warm React Query's cache for the neighbours' OWN siblings so when
  // the user lands on a neighbour, that photo's prev/next are ready
  // immediately — and so the ±2 large prefetch below can read them.
  useEffect(() => {
    for (const id of [prevId, nextId]) {
      if (!id) continue;
      void qc.prefetchQuery({
        queryKey: ['photo-siblings', id, filterYear, circleId ?? null, memoryId ?? null],
        queryFn: () => api.get<Siblings>(`/photos/${id}/siblings${siblingsQuery}`),
        staleTime: 5 * 60_000,
      });
    }
  }, [prevId, nextId, filterYear, circleId, memoryId, qc, siblingsQuery]);

  // Warm the cache for nearby large variants (±1 and ±2).
  //
  // We use detached Image() loaders, NOT <link rel="prefetch">. A link
  // lives in <head> and is torn down (`l.remove()`) on every navigation
  // when this effect re-runs — and removing a prefetch link can abort
  // the in-flight fetch the browser coalesced it with. The destination
  // of any swipe is a photo that was, a moment earlier, a neighbour
  // being prefetched; if its large bytes were still loading when we
  // navigated onto it, the link teardown aborted the very fetch its
  // now-centre <img> depended on, stranding it on its blurred thumb
  // ("the photo doesn't appear"). An Image() isn't bound to a DOM node,
  // so navigating away never cancels it, and it self-collects once the
  // load settles — no cleanup, nothing to remove.
  useEffect(() => {
    function warm(id: string | null | undefined) {
      if (!id) return;
      const im = new Image();
      im.decoding = 'async';
      im.fetchPriority = 'low';
      im.src = `/api/photos/${id}/large`;
    }

    // ±1 immediate neighbours.
    warm(prevId);
    warm(nextId);

    // ±2 (one beyond), read from the neighbours' cached siblings data we
    // kicked off above. Key includes circleId so the family/circle
    // universes don't cross. A cache miss just skips — the next render
    // after siblingsQ settles will warm it.
    const prevSibs = prevId
      ? qc.getQueryData<Siblings>([
          'photo-siblings',
          prevId,
          filterYear,
          circleId ?? null,
          memoryId ?? null,
        ])
      : undefined;
    const nextSibs = nextId
      ? qc.getQueryData<Siblings>([
          'photo-siblings',
          nextId,
          filterYear,
          circleId ?? null,
          memoryId ?? null,
        ])
      : undefined;
    // prev's prev = the photo 2 to the "older" side
    warm(prevSibs?.prev?.id);
    // next's next = the photo 2 to the "newer" side
    warm(nextSibs?.next?.id);
  }, [prevId, nextId, filterYear, circleId, memoryId, qc]);

  // Gesture + layout state.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [phase, setPhase] = useState<Phase>('idle');
  const [dx, setDx] = useState(0);
  const [snapTo, setSnapTo] = useState<-1 | 0 | 1>(0);
  const startX = useRef<number | null>(null);

  // Frame aspect ratio — derived from the center photo's natural
  // dimensions so the container fits the photo edge-to-edge instead
  // of letterboxing in a fixed square. Default 4/3 until first
  // measurement comes in; updates whenever centerId changes.
  const [aspect, setAspect] = useState<string>('4 / 3');

  // Measure container width — drives the strip layout. ResizeObserver
  // keeps us correct through orientation changes / window resizes.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.offsetWidth);
    const ro = new ResizeObserver(() => {
      if (containerRef.current) setWidth(containerRef.current.offsetWidth);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Safety net for out-of-band centerId changes (popstate, external
  // navigate). Resets gesture state so we never get stuck mid-drag.
  useLayoutEffect(() => {
    setDx(0);
    setSnapTo(0);
    setPhase('idle');
    startX.current = null;
  }, [centerId]);

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (phase !== 'idle' || !width) return;
    startX.current = e.clientX;
    setPhase('dragging');
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (phase !== 'dragging' || startX.current == null) return;
    let delta = e.clientX - startX.current;
    // Rubber-band resistance at library boundaries.
    if (delta < 0 && !nextId) delta = delta / 4;
    if (delta > 0 && !prevId) delta = delta / 4;
    setDx(delta);
  }

  function onPointerUp() {
    if (phase !== 'dragging') return;
    startX.current = null;
    const threshold = Math.max(60, width * 0.18);
    if (dx < -threshold && nextId) {
      setSnapTo(-1);
      setPhase('snapping');
    } else if (dx > threshold && prevId) {
      setSnapTo(1);
      setPhase('snapping');
    } else {
      setSnapTo(0);
      setPhase('snapping');
    }
  }

  function onTransitionEnd() {
    if (phase !== 'snapping') return;
    const dest = snapTo === -1 ? nextId : snapTo === 1 ? prevId : null;
    // `dest !== centerId` is belt-and-suspenders against stale siblings:
    // never commit a "swipe" that lands back on the photo already shown.
    if (dest && dest !== centerId) {
      // Atomic batch: gesture state resets to idle and the parent
      // commits the new centerId in the same React render.
      setPhase('idle');
      setDx(0);
      setSnapTo(0);
      onChange(dest);
    } else {
      // Snap-back finished.
      setPhase('idle');
      setDx(0);
    }
  }

  let translateX: number;
  if (phase === 'snapping') {
    translateX = snapTo === -1 ? -2 * width : snapTo === 1 ? 0 : -width;
  } else if (phase === 'dragging') {
    translateX = -width + dx;
  } else {
    translateX = -width;
  }

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden touch-pan-y select-none bg-paper"
      style={{
        aspectRatio: aspect,
        transition: 'aspect-ratio 200ms ease-out',
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {width > 0 && (
        <div
          ref={stripRef}
          className="flex absolute inset-0"
          style={{
            width: 3 * width,
            transform: `translate3d(${translateX}px, 0, 0)`,
            transition: phase === 'snapping' ? 'transform 240ms ease-out' : 'none',
            willChange: 'transform',
          }}
          onTransitionEnd={onTransitionEnd}
        >
          <Slot key={prevId ?? 'no-prev'} id={prevId} width={width} />
          <Slot
            key={centerId}
            id={centerId}
            width={width}
            alt={alt}
            priority
            onAspectMeasured={setAspect}
          />
          <Slot key={nextId ?? 'no-next'} id={nextId} width={width} />
        </div>
      )}
    </div>
  );
}

/**
 * One photo in the strip. Thumb underlay (blurred placeholder, paints
 * instantly from cache) + sharp large overlay that fades in over
 * 200ms when ready. Slots are keyed by photo id by the parent, so
 * every mount gets a fresh `<img>` with a live onLoad handler — no
 * stale-state race when the strip rebases.
 */
export function Slot({
  id,
  width,
  alt,
  priority,
  onAspectMeasured,
}: {
  id: string | null;
  width: number;
  alt?: string;
  priority?: boolean;
  onAspectMeasured?: (aspect: string) => void;
}) {
  const [largeReady, setLargeReady] = useState(false);
  // Bumped by onError to retry a failed/aborted large load with a fresh
  // URL (cache-busted), so a transient load failure can't leave the
  // photo permanently stranded on its blurred thumb. Bounded so a truly
  // missing object degrades to the blur instead of looping.
  const [largeAttempt, setLargeAttempt] = useState(0);
  const largeRef = useRef<HTMLImageElement | null>(null);
  const thumbRef = useRef<HTMLImageElement | null>(null);

  // Cached-image race: if the new src was already in the browser /
  // SW cache, `complete` may be true before React attaches the
  // onLoad listener. Check on mount and flip the flag if so.
  useEffect(() => {
    if (largeRef.current?.complete && largeRef.current.naturalWidth > 0) {
      setLargeReady(true);
    }
  }, []);

  // Measure the aspect ratio from the thumb (loads fast, same aspect
  // as the large), then bubble it up so the carousel container can
  // fit the photo edge-to-edge.
  function reportAspect(img: HTMLImageElement) {
    if (onAspectMeasured && img.naturalWidth > 0 && img.naturalHeight > 0) {
      onAspectMeasured(`${img.naturalWidth} / ${img.naturalHeight}`);
    }
  }
  useEffect(() => {
    if (thumbRef.current?.complete && thumbRef.current.naturalWidth > 0) {
      reportAspect(thumbRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!id) {
    return <div style={{ width, flex: '0 0 auto' }} aria-hidden />;
  }

  return (
    <div
      className="relative bg-paper overflow-hidden"
      style={{ width, flex: '0 0 auto' }}
    >
      <img
        ref={thumbRef}
        src={`/api/photos/${id}/thumb`}
        alt=""
        aria-hidden
        className="absolute inset-0 w-full h-full object-contain"
        style={{
          opacity: largeReady ? 0 : 1,
          transition: 'opacity 200ms ease-out',
          filter: 'blur(8px)',
          transform: 'scale(1.05)', // hide blur halo at edges
        }}
        decoding="async"
        draggable={false}
        onLoad={(e) => reportAspect(e.currentTarget)}
      />
      <img
        ref={largeRef}
        src={`/api/photos/${id}/large${largeAttempt ? `?retry=${largeAttempt}` : ''}`}
        alt={alt ?? ''}
        className="relative w-full h-full object-contain block"
        style={{
          opacity: largeReady ? 1 : 0,
          transition: 'opacity 200ms ease-out',
        }}
        onLoad={() => setLargeReady(true)}
        onError={() => setLargeAttempt((n) => (n < 2 ? n + 1 : n))}
        decoding="async"
        // @ts-expect-error fetchpriority is in the HTML spec but not yet React's types
        fetchpriority={priority ? 'high' : 'low'}
        draggable={false}
      />
    </div>
  );
}
